import {
  readBookingFlowCache,
  readStoredBookingSummary,
} from "./bookingFlowStorage";

const normalizeTutorIdentifier = (value) => String(value || "").trim();

const getBookingIdFromPayload = (payload = {}) =>
  String(
    payload?.booking_id ||
      payload?.bookingId ||
      payload?.id ||
      payload?.booking?.booking_id ||
      payload?.booking?.bookingId ||
      payload?.booking?.id ||
      ""
  ).trim();

const normalizeBookingStatusValue = (value) =>
  String(value || "").trim().toLowerCase();

const getBookingStatusFromPayload = (payload = {}) =>
  normalizeBookingStatusValue(payload?.status || payload?.booking?.status);

const getPaymentStatusFromPayload = (payload = {}) =>
  normalizeBookingStatusValue(
    payload?.payment_status || payload?.booking?.payment_status
  );

const getPaidAtFromPayload = (payload = {}) =>
  payload?.paid_at || payload?.booking?.paid_at || "";

const isBookingPaidOrConfirmed = (payload = {}) =>
  getPaymentStatusFromPayload(payload) === "paid" ||
  getBookingStatusFromPayload(payload) === "confirmed";

const normalizeHeldBookingPayload = (payload = {}) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const bookingId = getBookingIdFromPayload(payload);
  const nestedBooking =
    payload?.booking && typeof payload.booking === "object"
      ? payload.booking
      : null;

  return {
    ...nestedBooking,
    ...payload,
    booking_id: bookingId || nestedBooking?.booking_id || "",
    bookingId: bookingId || nestedBooking?.bookingId || "",
    slot:
      payload?.slot ||
      nestedBooking?.slot ||
      (payload?.slot_id ? { id: payload.slot_id } : undefined),
    year:
      payload?.year ||
      nestedBooking?.year ||
      (payload?.year_id ? { id: payload.year_id } : undefined),
    status: payload?.status || nestedBooking?.status || "",
    payment_status:
      payload?.payment_status || nestedBooking?.payment_status || "",
    paid_at: getPaidAtFromPayload(payload),
  };
};

const mergePendingSlotIntoAvailability = ({
  availabilitySlots = [],
  pendingBooking = null,
  fallbackSelectedSlot = null,
}) => {
  const baseSlots = Array.isArray(availabilitySlots) ? availabilitySlots : [];
  if (!pendingBooking || isBookingPaidOrConfirmed(pendingBooking)) {
    return baseSlots;
  }

  const pendingSlot =
    pendingBooking?.slot ||
    pendingBooking?.booking?.slot ||
    fallbackSelectedSlot ||
    null;

  if (!pendingSlot || typeof pendingSlot !== "object") {
    return baseSlots;
  }

  const pendingSlotId = String(
    pendingSlot?.slot_id || pendingSlot?.id || ""
  ).trim();
  const pendingStartTime = String(
    pendingSlot?.start_time || pendingSlot?.startTime || ""
  ).trim();

  if (!pendingSlotId && !pendingStartTime) {
    return baseSlots;
  }

  const alreadyPresent = baseSlots.some((slot) => {
    const slotId = String(slot?.slot_id || slot?.id || "").trim();
    const startTime = String(slot?.start_time || slot?.startTime || "").trim();

    return (
      (pendingSlotId && slotId === pendingSlotId) ||
      (pendingStartTime && startTime === pendingStartTime)
    );
  });

  if (alreadyPresent) {
    return baseSlots;
  }

  return [
    ...baseSlots,
    {
      ...pendingSlot,
      id: pendingSlot?.id || pendingSlotId,
      slot_id: pendingSlot?.slot_id || pendingSlotId,
      start_time: pendingSlot?.start_time || pendingSlot?.startTime,
      end_time: pendingSlot?.end_time || pendingSlot?.endTime,
      pending_booking_id: String(
        pendingBooking?.booking_id ||
          pendingBooking?.bookingId ||
          pendingBooking?.id ||
          pendingBooking?.booking?.booking_id ||
          pendingBooking?.booking?.bookingId ||
          pendingBooking?.booking?.id ||
          ""
      ).trim(),
      is_pending_payment: true,
    },
  ];
};

const getStoredBookingSummaries = () => {
  const bookingFlowCache = readBookingFlowCache();
  const cachedSummaries = Object.values(bookingFlowCache || {}).filter(
    (entry) => entry && typeof entry === "object"
  );
  const activeSummary = readStoredBookingSummary();

  return [activeSummary, ...cachedSummaries].filter(
    (entry) => entry && typeof entry === "object"
  );
};

const getSummaryTutorId = (summary = {}) =>
  normalizeTutorIdentifier(
    summary?.tutorId ||
      summary?.tutor?.id ||
      summary?.heldBooking?.tutor_id ||
      summary?.heldBooking?.tutor?.id ||
      summary?.heldBooking?.tutor?.teacher_id ||
      summary?.booking?.tutor_id ||
      summary?.booking?.tutor?.id ||
      ""
  );

export const getPendingTutorBookingsFromStorage = ({
  tutorId = "",
} = {}) => {
  const normalizedTutorId = normalizeTutorIdentifier(tutorId);
  const seenBookingIds = new Set();

  return getStoredBookingSummaries().reduce((result, summary) => {
    const summaryTutorId = getSummaryTutorId(summary);
    if (normalizedTutorId && summaryTutorId && summaryTutorId !== normalizedTutorId) {
      return result;
    }

    const bookingPayload = normalizeHeldBookingPayload(
      summary?.heldBooking || summary
    );
    const bookingId = getBookingIdFromPayload(bookingPayload);
    if (!bookingPayload || !bookingId || seenBookingIds.has(bookingId)) {
      return result;
    }

    if (isBookingPaidOrConfirmed(bookingPayload) || isBookingPaidOrConfirmed(summary)) {
      return result;
    }

    seenBookingIds.add(bookingId);
    result.push({
      tutorId: summaryTutorId,
      booking: bookingPayload,
      selectedSlot: summary?.selectedSlot || null,
    });
    return result;
  }, []);
};

export const mergePendingTutorSlotsFromStorage = ({
  tutorId = "",
  availabilitySlots = [],
} = {}) => {
  const pendingTutorBookings = getPendingTutorBookingsFromStorage({ tutorId });

  return pendingTutorBookings.reduce(
    (nextSlots, entry) =>
      mergePendingSlotIntoAvailability({
        availabilitySlots: nextSlots,
        pendingBooking: entry.booking,
        fallbackSelectedSlot: entry.selectedSlot,
      }),
    Array.isArray(availabilitySlots) ? availabilitySlots : []
  );
};
